<template>
  <div
    v-if="isVisible"
    ref="alertRef"
    class="alert"
    :class="[
      `alert--${data.colour || 'orange'}`,
      { 'alert--loading': isLoading },
    ]"
  >
    <div class="alert__inner ca-bullet-container">
      <!-- Loading state -->
      <template v-if="isLoading">
        <div class="alert__skeleton alert__skeleton--icon"></div>
        <div class="alert__skeleton alert__skeleton--text"></div>
        <div class="alert__skeleton alert__skeleton--icon-btn"></div>
      </template>

      <!-- Default state -->
      <template v-else>
        <div v-if="data.iconLeft" class="alert__icon" aria-hidden="true">
          <img class="alert__icon-img" :src="data.iconLeft" alt="" />
        </div>

        <component
          :is="normalizeHeadingTag(data.textHeadingType, 'p')"
          class="alert__text"
        >
          <span v-html="data.text"></span>
          <a
            v-if="data.link"
            class="alert__link"
            :href="data.link.url"
            :target="data.link.target || '_self'"
            rel="noopener noreferrer"
          >
            {{ data.link.text }}
          </a>
        </component>

        <button
          v-if="data.iconRight"
          class="alert__icon-btn"
          :aria-label="data.iconRight === 'close' ? 'Fechar' : 'Navegar'"
          @click="handleIconRightClick"
        >
          <img
            :src="iconMap[data.iconRight]"
            class="alert__icon-btn-img"
            alt=""
            aria-hidden="true"
          />
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  useTemplateRef,
} from "vue";
import iconCloseUrl from "@assets/Close_Icon-Button.svg?url";
import iconArrowUrl from "@assets/arrow-right-green.svg?url";
import { normalizeHeadingTag } from "../../../utils/heading";

const isLoading = ref(true);
const isVisible = ref(true);
const alertRef = useTemplateRef("alertRef");

const iconMap: Record<string, string> = {
  close: iconCloseUrl,
  arrow: iconArrowUrl,
};

const defaultModel = {
  text: "<strong>Atenção!</strong> O Crédito Agrícola nunca solicita dados pessoais ou credenciais de acesso por email, SMS ou telefone.",
  textHeadingType: "Paragraph",
  colour: "orange",
  iconLeft: "/assets/img/icons/seguranca.svg",
  iconRight: "close",
  link: {
    url: "#",
    text: "Saiba mais",
    target: "_self",
  },
};

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

const data = computed(() => props.model || defaultModel);

function handleIconRightClick() {
  if (data.value.iconRight === "close") {
    isVisible.value = false;
    updateBannerHeight();
  } else if (data.value.iconRight === "arrow" && data.value.link?.url) {
    window.open(data.value.link.url, data.value.link.target || "_self");
  }
}

let resizeObserver: ResizeObserver | null = null;

function updateBannerHeight() {
  const height = alertRef.value?.offsetHeight || 0;
  const bannerBottom = alertRef.value?.getBoundingClientRect().bottom ?? 0;
  const visibleOffset = bannerBottom > 0 ? Math.max(0, bannerBottom) : 0;
  document.documentElement.style.setProperty(
    "--alert-banner-height",
    `${visibleOffset}px`,
  );
}

function onScroll() {
  updateBannerHeight();
}

onMounted(() => {
  isLoading.value = false;
  nextTick(() => {
    updateBannerHeight();
    if (alertRef.value) {
      resizeObserver = new ResizeObserver(updateBannerHeight);
      resizeObserver.observe(alertRef.value);
    }
  });
  window.addEventListener("scroll", onScroll, { passive: true });
});

onBeforeUnmount(() => {
  document.documentElement.style.setProperty("--alert-banner-height", "0px");
  resizeObserver?.disconnect();
  window.removeEventListener("scroll", onScroll);
});
</script>

<style scoped lang="scss">
.alert {
  width: 100%;
  border-radius: 0;
  margin-bottom: 0;
  border: none;
  padding: 0;
}

.alert__inner {
  display: flex;
  align-items: center;
  gap: var(--size-4);
  padding-top: var(--size-4);
  padding-bottom: var(--size-4);
}

.alert--orange {
  background:
    linear-gradient(
      90deg,
      rgba(233, 108, 0, 0.2) 0%,
      rgba(233, 108, 0, 0.2) 100%
    ),
    #fff;
}

.alert--solarized {
  background: #f6f7f2;
}

.alert__icon-img {
  width: var(--size-6);
  height: var(--size-6);
  flex-shrink: 0;
  aspect-ratio: 1/1;
}

.alert__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--size-8);
  height: var(--size-8);
  flex-shrink: 0;
  border-radius: 100px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s ease;
}

.alert__icon-btn:disabled {
  cursor: default;
  opacity: 0.4;
}

/* Icon button states on orange background */
.alert--orange .alert__icon-btn:hover:not(:disabled) {
  background-color: var(--ca-color-neutrals-inverted-30);
}

.alert--orange .alert__icon-btn:active:not(:disabled) {
  background-color: var(--ca-color-neutrals-inverted-60);
}

.alert--orange .alert__icon-btn:focus-visible {
  outline: 1px solid var(--ca-info, #0a8bff);
  outline-offset: 2px;
}

/* Icon button states on solarized background */
.alert--solarized .alert__icon-btn:hover:not(:disabled) {
  background-color: var(--ca-color-hover-pressed-neutral-hover);
}

.alert--solarized .alert__icon-btn:active:not(:disabled) {
  background-color: var(--ca-color-hover-pressed-neutral-pressed);
}

.alert--solarized .alert__icon-btn:focus-visible {
  outline: 1px solid var(--ca-info, #0a8bff);
  outline-offset: 2px;
}

.alert__icon-btn-img {
  display: block;
}

.alert__icon-btn :deep(svg) {
  display: block;
}

.alert__text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  margin: 0;
  color: #333c45;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.192px;
}

.alert__link {
  color: #333c45;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.192px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

/* Loading skeleton */
.alert__skeleton {
  border-radius: var(--size-3);
  animation: shimmer 1.5s infinite;
  background-size: 200% 100%;
}

.alert--orange .alert__skeleton {
  background: linear-gradient(90deg, rgba(233, 108, 0, 0.16) 0%, #fbe2cc 100%);
  background-size: 200% 100%;
}

.alert--solarized .alert__skeleton {
  background: linear-gradient(90deg, #e5e3df 0%, #f6f7f2 100%);
  background-size: 200% 100%;
}

.alert__skeleton--icon {
  width: var(--size-6);
  height: var(--size-6);
  flex-shrink: 0;
  border-radius: 100px;
}

.alert__skeleton--text {
  flex: 1;
  height: 26px;
  min-width: 0;
}

.alert__skeleton--icon-btn {
  width: 43px;
  height: 43px;
  flex-shrink: 0;
  border-radius: 100px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (min-width: 1536px) {
  .alert__inner {
    padding-top: var(--size-3);
    padding-bottom: var(--size-3);
  }
}

@media (min-width: 1366px) and (max-width: 1535px) {
  .alert__inner {
    padding: var(--size-3) 44px;
  }
}

@media (min-width: 1024px) and (max-width: 1365px) {
  .alert__inner {
    padding: var(--size-3) 44px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .alert__inner {
    padding: var(--size-4) var(--size-6);
  }
}

@media (max-width: 767px) {
  .alert__inner {
    padding: var(--size-4) var(--size-5);
  }
  .alert__text {
    font-size: 16px;
    line-height: 20px;
  }
}
</style>
